import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { PROJECTS } from "@/data/site";

export const Gallery = () => {
  const [activeProject, setActiveProject] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);

  const project = activeProject !== null ? PROJECTS[activeProject] : null;

  const open = (i) => {
    setActiveProject(i);
    setImgIndex(0);
  };
  const close = useCallback(() => setActiveProject(null), []);

  const next = useCallback(() => {
    if (!project) return;
    setImgIndex((i) => (i + 1) % project.images.length);
  }, [project]);

  const prev = useCallback(() => {
    if (!project) return;
    setImgIndex((i) => (i - 1 + project.images.length) % project.images.length);
  }, [project]);

  useEffect(() => {
    if (activeProject === null) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [activeProject, close, next, prev]);

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="bg-navy-950 py-24 sm:py-32"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-16">
          <p className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-light mb-4">
            Our Work
          </p>
          <h2 className="font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Projects &
            <br />
            Finishes.
          </h2>
          <p className="mt-6 text-lg text-zinc-400">
            Tap any project to open the full photo set.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROJECTS.map((p, i) => (
            <button
              key={p.id}
              data-testid={`project-card-${p.id}`}
              onClick={() => open(i)}
              className="group relative overflow-hidden aspect-[4/3] border border-navy-700 text-left focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <img
                src={p.cover}
                alt={p.label}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/20 to-transparent opacity-85 group-hover:opacity-95 transition-opacity" />

              <span className="absolute top-4 right-4 flex items-center gap-1.5 bg-navy-950/80 backdrop-blur text-brand-light text-xs font-bold uppercase tracking-wide px-2.5 py-1.5 border border-navy-700">
                <Images className="w-3.5 h-3.5" />
                {p.images.length}
              </span>

              <div className="absolute bottom-0 left-0 p-6">
                <p className="font-display font-bold uppercase tracking-wide text-white text-base sm:text-lg">
                  {p.label}
                </p>
                <p className="mt-1 text-sm text-brand-light font-semibold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                  View Project →
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {project && (
          <motion.div
            data-testid="project-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-950/95 backdrop-blur-md p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute top-5 right-5 z-10 flex items-center justify-center w-12 h-12 bg-navy-800 hover:bg-brand hover:text-navy-950 text-white border border-navy-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>

            <motion.div
              className="relative w-full max-w-5xl"
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4">
                <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-brand-light">
                  {imgIndex + 1} / {project.images.length}
                </p>
                <h3 className="font-display font-black uppercase text-white text-2xl sm:text-3xl tracking-tight">
                  {project.label}
                </h3>
                <p className="text-sm text-zinc-400 mt-1">{project.blurb}</p>
              </div>

              <div className="relative bg-navy-900 border border-navy-700">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={imgIndex}
                    data-testid="lightbox-image"
                    src={project.images[imgIndex]}
                    alt={`${project.label} ${imgIndex + 1}`}
                    className="w-full max-h-[62vh] object-contain bg-navy-950"
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  />
                </AnimatePresence>

                {project.images.length > 1 && (
                  <>
                    <button
                      data-testid="lightbox-prev"
                      onClick={prev}
                      className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 bg-navy-950/80 hover:bg-brand hover:text-navy-950 text-white border border-navy-700 transition-colors"
                      aria-label="Previous"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      data-testid="lightbox-next"
                      onClick={next}
                      className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-11 h-11 bg-navy-950/80 hover:bg-brand hover:text-navy-950 text-white border border-navy-700 transition-colors"
                      aria-label="Next"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
              </div>

              {project.images.length > 1 && (
                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                  {project.images.map((img, idx) => (
                    <button
                      key={idx}
                      data-testid={`lightbox-thumb-${idx}`}
                      onClick={() => setImgIndex(idx)}
                      className={`shrink-0 w-20 h-16 overflow-hidden border-2 transition-colors ${
                        idx === imgIndex
                          ? "border-brand"
                          : "border-navy-700 opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={img}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
